import { BREAKPOINTS } from "configs/constants";
import {
  BgColors,
  BgPositions,
  BgSize,
  CfBackground,
  CfBgColors,
  CfBgPositions,
  CfBgSize,
  CfPropTypeDictionary,
} from "types/generic/types";
import { CfFile, CfFileImage } from "types/cf-utils/types";
import { CfImageProps } from "components/Image/types";
import { GenericImageSettings } from "types/types";
import { convertCfImageProps as convertCfImageForGatsbyImageProps } from "components/Image/CfImage";
import Image from "components/Image";
import React from "react";

const CF_BACKGROUND_COLOR_DICTIONARY: CfPropTypeDictionary<
  CfBgColors,
  BgColors
> = {
  white: "white",
  black: "black",
  whiteTransparent: "white",
  blackTransparent: "blackTransparent",
  primary: "primary",
  secondary: "secondary",
  gradientTpay: "gradientTpay",
  gradientTpayV2: "gradientTpayV2",
  gradientTpayVertical: "gradientTpayVertical",
  gradientTpayTopLeft: "gradientTpayTopLeft",
  gradientTpayTopRight: "gradientTpayTopRight",
  gradientTpayBottomLeft: "gradientTpayBottomLeft",
  gradientTpayBottomRight: "gradientTpayBottomRight",
  gradientBrandBlue: "gradientBrandBlue",
  accent: "accent",
  neutralPrimary: "neutralPrimary",
  neutralSecondary: "neutralSecondary",
  neutralTertiary: "neutralTertiary",
  neutralQuaternary: "neutralQuaternary",
  brandPrimary: "brandPrimary",
  brandSecondary: "brandSecondary",
  success: "success",
  warning: "warning",
  error: "error",
};

const CF_BACKGROUND_POSITION_DICTIONARY: CfPropTypeDictionary<
  CfBgPositions,
  BgPositions
> = {
  initial: "initial",
  "left top": "lt",
  "left center": "lc",
  "left bottom": "lb",
  "right top": "rt",
  "right center": "rc",
  "right bottom": "rb",
  "center top": "ct",
  "center center": "cc",
  "center bottom": "cb",
};

const CF_BACKGROUND_SIZE_DICTIONARY: CfPropTypeDictionary<CfBgSize, CfBgSize> =
  {
    auto: "auto",
    cover: "cover",
    contain: "contain",
  };

export const convertCfBackgroundColorProps = (
  cfProp: CfBgColors | undefined | null,
): BgColors => {
  if (cfProp) {
    return CF_BACKGROUND_COLOR_DICTIONARY[cfProp] as BgColors;
  }
  return "";
};

export const convertCfImageProps = (cfProp: CfFile | undefined | null) => {
  if (cfProp) {
    return cfProp.file.url;
  }
  return "";
};

type ConvertCfBgPositionsPropsReturn = BgPositions | undefined;

export const convertCfBackgroundPosition = (
  cfProp: CfBgPositions | undefined | null,
): ConvertCfBgPositionsPropsReturn => {
  if (cfProp) {
    return CF_BACKGROUND_POSITION_DICTIONARY[cfProp] as BgPositions;
  }
  return undefined;
};

type ConvertCfBgSizePropsReturn = BgSize | undefined;

export const convertCfBackgroundSize = (
  cfProp: CfBgSize | undefined | null,
): ConvertCfBgSizePropsReturn => {
  if (cfProp) {
    return CF_BACKGROUND_SIZE_DICTIONARY[cfProp] as BgSize;
  }
  return "";
};

export const convertCfBackgroundProps = (
  cfProp: CfBackground | undefined | null,
) => {
  if (!cfProp) return undefined;
  return {
    backgroundColor: convertCfBackgroundColorProps(
      cfProp.backgroundColor,
    ) as BgColors,
    backgroundImage: convertCfImageProps(cfProp.backgroundImage),
    backgroundImageMobile: convertCfImageProps(cfProp.backgroundImageMobile),
    backgroundPosition: convertCfBackgroundPosition(
      cfProp.backgroundPosition,
    ) as BgPositions,
    backgroundSize: convertCfBackgroundSize(cfProp.backgroundSize) as BgSize,
  };
};

export const convertCfBackgroundForGatsbyImageProps = (
  cfProp: CfBackground | null | undefined,
  { breakpoint, ...settings }: GenericImageSettings = {},
) => {
  if (!cfProp) return undefined;
  const {
    backgroundColor,
    backgroundImage,
    backgroundImageMobile,
    backgroundPosition,
    backgroundSize,
    ...otherProps
  } = cfProp;

  const image = {
    ...otherProps,
    ...settings,
    breakpoint: breakpoint || BREAKPOINTS.md,
    imageDesktop: backgroundImage as CfFileImage,
    imageMobile: (backgroundImageMobile as CfFileImage) || undefined,
  };

  return {
    backgroundColor: convertCfBackgroundColorProps(backgroundColor) as BgColors,
    image: image.imageDesktop && (
      <Image {...convertCfImageForGatsbyImageProps({ ...image })} />
    ),
    backgroundPosition: convertCfBackgroundPosition(
      backgroundPosition,
    ) as BgPositions,
    backgroundSize: convertCfBackgroundSize(backgroundSize) as BgSize,
  };
};

export const convertCfAllImgProps = (cfProp: CfImageProps) => {
  return {
    alt: cfProp?.alt || "",
    slug: cfProp?.slug || "",
    imageDesktop: convertCfImageProps(cfProp?.imageDesktop),
    imageMobile: convertCfImageProps(cfProp?.imageMobile),
  };
};
